import { PerspectiveCamera, OrthographicCamera, Scene } from "three";
import Size from "../../utils/size";
import type ThreeJSApp from "../../app";
import { defaultCameraConfig } from '../../config/default.config';
import type { CameraConfig } from "./type";
import {
  createCamerasFromConfig,
  findActiveCameraConfig,
  findCameraConfigByName,
} from './core';
import {
  calculateCanvasAspectRatio,
  resizeAllCameras,
  addAllCamerasToScene,
  cleanupAllCameras,
} from './util';

export default class Camera {

  root: ThreeJSApp;
  size: Size;
  scene: Scene;
  canvas: HTMLCanvasElement;
  cameras: Map<string, PerspectiveCamera | OrthographicCamera> = new Map();
  activeCamera!: PerspectiveCamera | OrthographicCamera;
  activeCameraName: string = "";
  private cameraConfig: CameraConfig;

  constructor(root: ThreeJSApp) {
    this.root = root;
    this.size = this.root.size;
    this.scene = this.root.scene;
    this.canvas = this.root.canvas;
    this.cameraConfig = defaultCameraConfig;

    this.setCameras();
  }

  setCameras() {
    const aspectRatio = calculateCanvasAspectRatio(this.canvas, this.size.width, this.size.height);

    // 모든 카메라 생성
    this.cameras = createCamerasFromConfig(this.cameraConfig, aspectRatio);

    // 모든 카메라를 씬에 추가
    addAllCamerasToScene(this.cameras, this.scene);

    // 활성 카메라 설정
    this.setActiveCamera();
  }

  // 카메라 설정 동적 업데이트
  updateConfig(newConfig: CameraConfig) {
    

    // 기존 카메라 정리
    cleanupAllCameras(this.cameras, this.scene);

    // 새로운 설정 적용
    this.cameraConfig = newConfig;

    // 새로운 카메라 생성
    this.setCameras();

    
  }

  setActiveCamera(cameraName?: string) {
    let targetConfig;

    if (cameraName) targetConfig = findCameraConfigByName(this.cameraConfig, cameraName);

    if (!targetConfig) targetConfig = findActiveCameraConfig(this.cameraConfig);

    if (!targetConfig) {
      throw new Error('활성 카메라를 찾을 수 없습니다.');
    }

    const camera = this.cameras.get(targetConfig.name);

    if (!camera) {
      throw new Error(`카메라를 찾을 수 없습니다.`);
    }

    this.activeCamera = camera;
    this.activeCameraName = targetConfig.name;
  }

  // 카메라 전환
  switchCamera(cameraName: string): boolean {
    const camera = this.cameras.get(cameraName);
    if (!camera) {
      console.warn(`🚨 카메라 '${cameraName}'을 찾을 수 없습니다.`);
      return false;
    }

    this.activeCamera = camera;
    this.activeCameraName = cameraName;

    

    return true;
  }

  // 현재 활성 카메라 반환
  getCurrentCamera(): PerspectiveCamera | OrthographicCamera {
    return this.activeCamera;
  }

  // 특정 카메라 반환
  getCamera(cameraName: string): PerspectiveCamera | OrthographicCamera | undefined {
    return this.cameras.get(cameraName);
  }

  // 모든 카메라 이름 반환
  getCameraNames(): string[] {
    return Array.from(this.cameras.keys());
  }

  // 렌더러 호환성을 위한 perspectiveCamera getter
  get perspectiveCamera(): PerspectiveCamera {
    const camera = this.getCurrentCamera();
    if (camera instanceof PerspectiveCamera) {
      return camera;
    }
    throw new Error(`현재 활성 카메라는 PerspectiveCamera가 아닙니다: ${this.activeCameraName}`);
  }

  resize(width?: number, height?: number) {
    //TODO: 이건 렌더러에도 필요한 내용인듯
    const aspectRatio = calculateCanvasAspectRatio(this.canvas, width, height);

    // 모든 카메라 리사이즈
    resizeAllCameras(this.cameras, aspectRatio);

    
  }

  destroy() {
    cleanupAllCameras(this.cameras, this.scene);
    
  }
}